import React from 'react';
import {
  GridFilterModel,
  GridLinkOperator,
  GridRenderCellParams,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import AdditionalActions from './cells/AdditionalActions';
import { ProjectTableType } from './types';
import MultilineText from './cells/MultilineText';
import FieldAndId from './cells/TitleAndId';
import Date from './cells/Date';
import ProfileAvatar from '../ProfileAvatar';
import DateAndTime from './cells/DateAndTime';
import CountWithWarning from './cells/CountWithWarning';
import ContentInsightsCard from '../ContentInsightsCard';
import { getInsights } from '../ContentInsightsCard/utils';

export const TABLE_DEFAULT_SHOWN_ITEMS = 100;
export const TABLE_ROW_HEIGHT = 62;
export const TABLE_HEADER_HEIGHT = 42;
export const TABLE_ADDITIONAL_ACTIONS_CELL_WIDTH = 52;

export const filteringModel = (
  items: { columnField: string; value: string | undefined; operatorValue: string }[],
): GridFilterModel => ({
  items: items,
  linkOperator: GridLinkOperator.And,
});

const columnConstants = {
  caseNumberDefaultWidth: 160,
  firebaseIdWidth: 260,
  dateColumnWidth: 200,
  lessonStatusWidth: 120,
  authorImageWith: 60,
  insightsWidth: 70,
};

const dateColumn = (fieldTitle: string, headerName: string) => {
  return {
    field: fieldTitle,
    headerName: headerName,
    renderCell: (params: GridRenderCellParams) => <Date params={params} />,
    width: columnConstants.dateColumnWidth,
  };
};

const dateAndTimeColumn = (fieldTitle: string, headerName: string) => {
  return {
    field: fieldTitle,
    headerName: headerName,
    renderCell: (params: GridRenderCellParams) => <DateAndTime params={params} />,
    width: columnConstants.dateColumnWidth,
  };
};

const tableColumnsUtils = {
  insightsColumn: (contentType: 'lesson' | 'contentGroup') => {
    return {
      field: 'insights',
      headerName: 'Insights',
      width: columnConstants.insightsWidth,
      valueGetter: (params: GridValueGetterParams) =>
        getInsights(params.row?.fullContentData, contentType)?.length || 0,
      renderCell: (params: GridRenderCellParams) => (
        <ContentInsightsCard params={params} contentType={contentType} cardStyle={'cell'} />
      ),
      sortComparator: (v1: number, v2: number) => v1 - v2,
    };
  },
  additionalActionsColumn: ({
    onEdit,
    onDelete,
    onArchive,
    onDisable,
    onEnable,
    onViewDetails,
    onRemove,
    onMoveUp,
    onMoveDown,
  }: {
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onArchive?: (id: string) => void;
    onDisable?: (id: string, userId: string) => void;
    onEnable?: (id: string, userId: string) => void;
    onViewDetails?: (id: string) => void;
    onRemove?: (id: string) => void;
    onMoveUp?: (id: string) => void;
    onMoveDown?: (id: string) => void;
  }) => ({
    field: 'additionalActions',
    headerName: '',
    renderCell: (params: GridRenderCellParams) => (
      <AdditionalActions
        onEdit={onEdit}
        onDelete={onDelete}
        onArchive={onArchive}
        onDisable={onDisable}
        onEnable={onEnable}
        onViewDetails={onViewDetails}
        onRemove={onRemove}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        params={params}
      />
    ),
    width: TABLE_ADDITIONAL_ACTIONS_CELL_WIDTH,
  }),
  primaryAction: (primaryActionMethod: (id: string) => void) => ({
    primaryAction: (params: GridRowParams) => primaryActionMethod(params.id as string),
  }),
  multilineText: (field: string, headerTitle: string) => ({
    field,
    headerName: headerTitle,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => <MultilineText text={params.value} />,
  }),
  customTitle: (field?: string, headerTitle?: string, fieldPropertyName?: string, flex?: number) => ({
    field: field || 'title',
    headerName: headerTitle || 'Title',
    flex: flex || 2,
    renderCell: (params: GridRenderCellParams) => (
      <FieldAndId params={{ ...params, fieldPropertyName: fieldPropertyName || 'title' }} />
    ),
  }),
  lessonTitle: () => tableColumnsUtils.customTitle(),
  contentGroupTitle: () => tableColumnsUtils.customTitle(),
  campaignName: () => tableColumnsUtils.customTitle('name', 'Campaign', 'name'),
  authorName: () => tableColumnsUtils.customTitle('name', 'Mentor', 'name'),
  categoryName: () => tableColumnsUtils.customTitle('name', 'Category', 'name'),
  goalName: () => tableColumnsUtils.customTitle('name', 'Goal', 'name'),
  questionText: () => tableColumnsUtils.customTitle('text', 'Question text', 'text', 4),
  pillarName: () => tableColumnsUtils.customTitle('name', 'Pillar type', 'name'),
  promotionName: () => tableColumnsUtils.customTitle('title', 'Promotion', 'title'),
  authorImage: () => ({
    field: 'image',
    headerName: '',
    width: columnConstants.authorImageWith,
    renderCell: (params: GridRenderCellParams) => (
      <>
        {params?.row?.imagePath && params?.row?.imageUrl ? (
          <ProfileAvatar size={42} initials={params?.row?.initials} imageUrl={params?.row?.imageUrl} />
        ) : (
          <></>
        )}
      </>
    ),
  }),
  dateColumn,
  dateAndTimeColumn,
  searchValueColumn: {
    field: 'searchValue',
    headerName: 'Row data',
    hide: true,
  },
};

export const tableColumns = {
  lessons: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      { field: 'status', headerName: 'Status', width: columnConstants.lessonStatusWidth },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('lesson'),
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'lessons' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),
  lessonStats: () => ({
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      { field: 'totalScore', headerName: 'Total Score', flex: 1 },
      {
        field: 'likes',
        headerName: 'Likes',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.like || 0,
      },
      {
        field: 'superLikes',
        headerName: 'Super Likes',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.superLike || 0,
      },
      {
        field: 'dislikes',
        headerName: 'Dislikes',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.dislike || 0,
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.takeNote || 0,
      },
      {
        field: 'favorites',
        headerName: 'Favorites',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.addFavorite || 0,
      },
      {
        field: 'completed',
        headerName: 'Completed',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.completed || 0,
      },
      {
        field: 'plays',
        headerName: 'Plays',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.play || 0,
      },
      {
        field: 'skips',
        headerName: 'Skips',
        flex: 1,
        valueGetter: (params: any) => params.row.actionCounts?.skip || 0,
      },
      {
        field: 'lastUpdated',
        headerName: 'Last Updated',
        flex: 1,
        valueGetter: (params: any) => params.row.lastUpdated?.toLocaleDateString() || 'N/A',
      },
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'lessonStats' as ProjectTableType,
  }),
  lessonsPreview: {
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      { field: 'status', headerName: 'Status', width: columnConstants.lessonStatusWidth },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('lesson'),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'lessons' as ProjectTableType,
  },
  lessonsInCollection: ({
    onEdit,
    onRemove,
    onMoveUp,
    onMoveDown,
  }: {
    onEdit: (id: string) => void;
    onRemove: (id: string) => void;
    onMoveUp?: (id: string) => void;
    onMoveDown?: (id: string) => void;
  }) => ({
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      { field: 'status', headerName: 'Status', width: columnConstants.lessonStatusWidth },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('lesson'),
      tableColumnsUtils.additionalActionsColumn({ onEdit, onRemove, onMoveUp, onMoveDown }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'lessons' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  collections: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'collections' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  collectionsInSection: ({
    onRemove,
    onMoveUp,
    onMoveDown,
  }: {
    onRemove: (id: string) => void;
    onMoveUp: (id: string) => void;
    onMoveDown: (id: string) => void;
  }) => ({
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.additionalActionsColumn({ onRemove, onMoveUp, onMoveDown }),
    ],
    type: 'collections' as ProjectTableType,
  }),

  collectionsPreview: {
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'collections' as ProjectTableType,
  },
  collectionsInBundle: ({
    onEdit,
    onRemove,
    onMoveUp,
    onMoveDown,
  }: {
    onEdit: (id: string) => void;
    onRemove: (id: string) => void;
    onMoveUp?: (id: string) => void;
    onMoveDown?: (id: string) => void;
  }) => ({
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.additionalActionsColumn({ onEdit, onRemove, onMoveUp, onMoveDown }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'collections' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  bundles: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'bundles' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  bundlesInSection: ({
    onRemove,
    onMoveUp,
    onMoveDown,
  }: {
    onRemove: (id: string) => void;
    onMoveUp: (id: string) => void;
    onMoveDown: (id: string) => void;
  }) => ({
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.additionalActionsColumn({ onRemove, onMoveUp, onMoveDown }),
    ],
    type: 'bundles' as ProjectTableType,
  }),

  bundlesPreview: {
    columns: [
      tableColumnsUtils.contentGroupTitle(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'pillarType', headerName: 'Pillar', flex: 1 },
      { field: 'pillarExpertise', headerName: 'Pillar expertise', flex: 1 },
      tableColumnsUtils.dateColumn('publishedAt', 'Publish date'),
      tableColumnsUtils.insightsColumn('contentGroup'),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'bundles' as ProjectTableType,
  },

  profileSubscriptionIntervals: {
    columns: [
      tableColumnsUtils.dateAndTimeColumn('purchaseDate', 'Started at'),
      tableColumnsUtils.dateAndTimeColumn('expirationDate', 'Expired at'),
    ],
    type: 'profileSubscriptionIntervals' as ProjectTableType,
  },

  profileSessionPlayback: {
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'type', headerName: 'Type', flex: 1 },
      { field: 'isFinished', headerName: 'Finished at least once', flex: 1 },
      { field: 'progressPercent', headerName: 'Last progress (%)', flex: 1 },
      { field: 'userProgress', headerName: 'Last progress (s)', flex: 1 },
      { field: 'totalLength', headerName: 'Total length (s)', flex: 1 },
      tableColumnsUtils.dateAndTimeColumn('updateDate', 'Last playback at'),
    ],
    type: 'profileSessionPlayback' as ProjectTableType,
  },
  profileContentRating: {
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'type', headerName: 'Type', flex: 1 },
      { field: 'rating', headerName: 'Rating', flex: 1 },
      tableColumnsUtils.dateAndTimeColumn('updateDate', 'Last playback at'),
    ],
    type: 'profileContentRating' as ProjectTableType,
  },
  profileLinkedContentBasic: {
    columns: [
      tableColumnsUtils.lessonTitle(),
      { field: 'type', headerName: 'Type', flex: 1 },
      tableColumnsUtils.dateAndTimeColumn('updateDate', 'Last playback at'),
    ],
    type: 'profileContentRating' as ProjectTableType,
  },
  profileGifts: ({ onRemove }: { onRemove: (id: string) => void }) => ({
    columns: [
      { field: 'type', headerName: 'Type', flex: 1 },
      { field: 'context', headerName: 'Context', flex: 2 },
      tableColumnsUtils.dateColumn('end', 'End date'),
      tableColumnsUtils.additionalActionsColumn({ onRemove }),
    ],
    type: 'profileDeals' as ProjectTableType,
  }),

  basicSingleText: (columnTitle: string) => ({
    columns: [{ field: 'text', headerName: columnTitle, flex: 1 }],
    type: 'basicSingleText' as ProjectTableType,
  }),

  campaigns: ({ onEdit, onDelete }: { onEdit: (id: string) => void; onDelete: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.campaignName(),
      { field: 'createdAt', headerName: 'Created at', flex: 1 },
      { field: 'updatedAt', headerName: 'Updated at', flex: 1 },
      { field: 'branchUrl', headerName: 'Branch URL', flex: 2 },
      { field: 'offering', headerName: 'Offering', flex: 2 },
      { field: 'active', headerName: 'State', flex: 1 },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'campaigns' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  authors: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.authorImage(),
      tableColumnsUtils.authorName(),
      { field: 'category', headerName: 'Category', flex: 1 },
      { field: 'bio', headerName: 'Bio', flex: 5 },
      {
        field: 'coursesCount',
        headerName: 'Courses',
        type: 'number',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <CountWithWarning count={params.row?.coursesCount || 0} warn={!params.row?.coursesCount} />
        ),
      },
      { field: 'lessonsCount', headerName: 'Lessons', type: 'number', flex: 1 },
      { field: 'qaCount', headerName: 'Q&A', type: 'number', flex: 1 },
      { field: 'shortsCount', headerName: 'Shorts', type: 'number', flex: 1 },
      dateColumn('releaseDate', 'Release date'),
      {
        field: 'isActive',
        headerName: 'Active',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => (params.row?.isActive !== false ? 'Yes' : 'No'),
      },
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'authors' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  categories: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.categoryName(),
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'categories' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  authorCategories: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.categoryName(),
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'authorCategories' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  goals: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.goalName(),
      { field: 'linkedPillars', headerName: 'Linked pillars', flex: 2 },
      { field: 'linkedCategories', headerName: 'Linked categories', flex: 4 },
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'goals' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),
  goalsPreview: {
    columns: [
      tableColumnsUtils.categoryName(),
      { field: 'linkedPillars', headerName: 'Linked pillars', flex: 2 },
      { field: 'linkedCategories', headerName: 'Linked categories', flex: 4 },
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'goalsPreview' as ProjectTableType,
  },
  goalsInSections: ({
    onRemove,
    onMoveUp,
    onMoveDown,
  }: {
    onRemove: (id: string) => void;
    onMoveUp: (id: string) => void;
    onMoveDown: (id: string) => void;
  }) => ({
    columns: [
      tableColumnsUtils.categoryName(),
      { field: 'linkedPillars', headerName: 'Linked pillars', flex: 2 },
      { field: 'linkedCategories', headerName: 'Linked categories', flex: 4 },
      tableColumnsUtils.additionalActionsColumn({ onRemove, onMoveUp, onMoveDown }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'goals' as ProjectTableType,
  }),

  reflectionQuestions: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.questionText(),
      { field: 'quoteAuthor', headerName: 'Quote author', flex: 2 },
      { field: 'quoteText', headerName: 'Quote text', flex: 4 },
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'reflectionQuestions' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  journalQuestions: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.questionText(),
      { field: 'questionType', headerName: 'Type', flex: 2 },
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'journalQuestions' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  pillarSurveyQuestions: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.questionText(),
      { field: 'key', headerName: 'Key', flex: 2 },
      { field: 'pillar', headerName: 'Pillar', flex: 2 },
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'pillarSurveyQuestions' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  pillars: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [tableColumnsUtils.pillarName(), tableColumnsUtils.additionalActionsColumn({ onEdit })],
    type: 'pillars' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  purchases: {
    columns: [
      { field: 'id', headerName: 'ID', flex: 1 },
      tableColumnsUtils.dateAndTimeColumn('createdAt', 'Created at'),
      { field: 'context', headerName: 'Context', flex: 1 },
      { field: 'status', headerName: 'Status', flex: 1 },
      { field: 'productId', headerName: 'Product ID', flex: 1 },
    ],
    type: 'purchases' as ProjectTableType,
  },
  transactions: {
    columns: [
      { field: 'id', headerName: 'ID', flex: 1 },
      tableColumnsUtils.dateAndTimeColumn('createdAt', 'Created at'),
      { field: 'context', headerName: 'Context', flex: 1 },
      { field: 'status', headerName: 'Status', flex: 1 },
      { field: 'productId', headerName: 'Product ID', flex: 1 },
    ],
    type: 'transactions' as ProjectTableType,
  },

  promotions: ({ onEdit }: { onEdit: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.promotionName(),
      { field: 'offerId', headerName: 'Offer ID', flex: 1 },
      tableColumnsUtils.dateColumn('startDate', 'Start date'),
      tableColumnsUtils.dateColumn('endDate', 'End date'),
      tableColumnsUtils.additionalActionsColumn({ onEdit }),
    ],
    type: 'promotions' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  dailyMissionTemplates: ({ onEdit, onDelete }: { onEdit: (id: string) => void; onDelete: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.customTitle('title', 'Mission', 'title'),
      {
        field: 'targetType',
        headerName: 'Target type',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) =>
          params.row?.targetType === 'minutesLearned' ? 'Minutes learned' : 'Sessions started',
      },
      { field: 'targetValue', headerName: 'Target value', flex: 1 },
      { field: 'order', headerName: 'Order', flex: 1 },
      {
        field: 'isActive',
        headerName: 'Active',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => (params.row?.isActive ? 'Yes' : 'No'),
      },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'dailyMissionTemplates' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  mentorShorts: ({ onEdit, onDelete }: { onEdit: (id: string) => void; onDelete: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.customTitle('title', 'Short', 'title'),
      { field: 'authorName', headerName: 'Mentor', flex: 1 },
      {
        field: 'durationSeconds',
        headerName: 'Length',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => {
          const total = Number(params.row?.durationSeconds) || 0;
          return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
        },
      },
      {
        field: 'isActive',
        headerName: 'Active',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => (params.row?.isActive ? 'Yes' : 'No'),
      },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'mentorShorts' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  levelDefinitions: ({ onEdit, onDelete }: { onEdit: (id: string) => void; onDelete: (id: string) => void }) => ({
    columns: [
      { field: 'level', headerName: 'Level', flex: 1 },
      tableColumnsUtils.customTitle('title', 'Title', 'title'),
      { field: 'minPoints', headerName: 'Min points', flex: 1 },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'levelDefinitions' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  mentorBoards: ({
    onEdit,
    onDelete,
    onMoveUp,
    onMoveDown,
  }: {
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    onMoveUp: (id: string) => void;
    onMoveDown: (id: string) => void;
  }) => ({
    columns: [
      tableColumnsUtils.customTitle('title', 'Board', 'title'),
      { field: 'subtitle', headerName: 'Subtitle', flex: 2 },
      { field: 'memberCount', headerName: 'Members', flex: 1 },
      {
        field: 'isActive',
        headerName: 'Active',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => (params.row?.isActive ? 'Yes' : 'No'),
      },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete, onMoveUp, onMoveDown }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'mentorBoards' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  networks: ({ onEdit, onDelete }: { onEdit: (id: string) => void; onDelete: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.customTitle('name', 'Name', 'name'),
      { field: 'networkId', headerName: 'Network ID', flex: 2 },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'networks' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),

  userPaths: ({ onEdit, onDelete }: { onEdit: (id: string) => void; onDelete: (id: string) => void }) => ({
    columns: [
      tableColumnsUtils.customTitle('title', 'Title', 'title'),
      {
        field: 'paths',
        headerName: 'Paths',
        flex: 2,
        valueGetter: (params: GridValueGetterParams) => params.row?.paths?.length || 0,
      },
      tableColumnsUtils.additionalActionsColumn({ onEdit, onDelete }),
      tableColumnsUtils.searchValueColumn,
    ],
    type: 'userPaths' as ProjectTableType,
    ...tableColumnsUtils.primaryAction(onEdit),
  }),
};
